import React, { useRef, useState, useEffect, useCallback } from 'react';

// Simple custom audio player that doesn't use WaveSurfer
const AudioPlayer = React.forwardRef((props, ref) => {
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const progressRef = useRef(null);
  const animationRef = useRef(null);
  const audioPath = '/reviewer/audioBook.mp3';

  // Expose cleanup method via ref
  React.useImperativeHandle(ref, () => ({
    cleanup: () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  }));

  useEffect(() => {
    const audio = new Audio(audioPath);
    audioRef.current = audio;
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setLoading(false);
    };
    
    const handleError = () => {
      setError('Failed to load audio');
      setLoading(false);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
    };
    
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);
    audio.addEventListener('ended', handleEnded);
    
    // Start loading the audio
    audio.load();
    
    // Cleanup
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioPath]);

  // Animation for progress bar
  const animateProgress = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      
      // Update progress bar visually
      if (progressRef.current && duration > 0) {
        const progressPercent = (audioRef.current.currentTime / duration) * 100;
        progressRef.current.style.width = `${progressPercent}%`;
      }
      
      if (isPlaying) {
        animationRef.current = requestAnimationFrame(animateProgress);
      }
    }
  }, [isPlaying, duration]);

  useEffect(() => {
    if (isPlaying) {
      animationRef.current = requestAnimationFrame(animateProgress);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, animateProgress]);

  const handlePlayPause = useCallback(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(err => {
            console.error('Play error:', err);
            setError('Playback failed');
          });
      }
    }
  }, [isPlaying]);

  const handleVolumeChange = (event) => {
    const newVolume = parseFloat(event.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const skipTime = (seconds) => {
    if (audioRef.current) {
      const newTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleProgressClick = (e) => {
    if (audioRef.current && duration > 0 && progressRef.current) {
      const progressBar = e.currentTarget;
      const clickPosition = e.clientX - progressBar.getBoundingClientRect().left;
      const progressWidth = progressBar.offsetWidth;
      const newTime = (clickPosition / progressWidth) * duration;
      
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Format time in minutes:seconds
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  if (error) {
    return (
      <div className="audio-player" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <div>Audio player unavailable</div>
        <button 
          onClick={() => window.location.reload()} 
          style={{ marginTop: '10px', padding: '5px 10px' }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="audio-player" style={{ maxWidth: '600px', margin: '0 auto' }}>
      {/* Custom Progress Bar */}
      <div 
        className="progress-container" 
        onClick={handleProgressClick}
        style={{
          height: '8px',
          backgroundColor: '#ddd',
          borderRadius: '4px',
          cursor: 'pointer',
          marginBottom: '15px',
          position: 'relative'
        }}
      >
        <div 
          ref={progressRef}
          className="progress-bar"
          style={{
            height: '100%',
            backgroundColor: '#ff4e09',
            borderRadius: '4px',
            width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
            transition: 'width 0.1s linear'
          }}
        />
      </div>
      
      <div className="time-display" style={{
        display: 'flex', 
        justifyContent: 'space-between', 
        fontSize: '14px', 
        color: '#666',
        marginBottom: '15px'
      }}>
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>
      
      <div className="controls" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '15px',
        flexWrap: 'wrap'
      }}>
        <button 
          onClick={() => skipTime(-10)} 
          className="skip-backward-button"
          disabled={loading}
          style={{
            background: 'none', 
            border: 'none', 
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1
            , backgroundColor: '#ffffff'
          }}
        >
          <img 
            src="/images/icons/player/back10.png" 
            alt="Rewind 10s" 
            style={{width: '32px', height: '32px', backgroundColor: '#ffffff'}}
          />
        </button>
        
        <button 
          onClick={handlePlayPause} 
          className="play-pause-button"
          disabled={loading}
          style={{
            background: 'none', 
            border: 'none', 
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1, backgroundColor: '#ffffff'
          }}
        >
          <img
            src={isPlaying ? '/images/icons/player/pause10.png' : '/images/icons/player/play10.png'}
            alt={isPlaying ? 'Pause' : 'Play'}
            style={{width: '40px', height: '40px', backgroundColor: '#ffffff'}}
          />
        </button>
        
        <button 
          onClick={() => skipTime(10)} 
          className="skip-forward-button"
          disabled={loading}
          style={{
            background: 'none', 
            border: 'none', 
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1
            , backgroundColor: '#ffffff'
          }}
        >
          <img 
            src="/images/icons/player/next10.png" 
            alt="Forward 10s" 
            style={{width: '32px', height: '32px', backgroundColor: '#ffffff'}}
          />
        </button>
        
        <div className="volume-control" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px'
        }}>
          <label htmlFor="volume" style={{display: 'flex', alignItems: 'center'}}>
            <img 
              src="/images/icons/player/high-volume.png" 
              style={{width: '24px', height: '24px', backgroundColor: '#ffffff', opacity: loading ? 0.5 : 1}}
              alt="Volume"
            />
          </label>
          <input
            id="volume"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            disabled={loading}
            style={{
              width: '80px', 
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.5 : 1
            }}
          />
        </div>
      </div>
      
      {loading && (
        <div style={{ textAlign: 'center', color: '#666', marginTop: '10px' }}>
          Loading audio...
        </div>
      )}
    </div>
  );
});

export default AudioPlayer;